import type { CartLineItem } from '@/types/checkout';

/** Stronger tint when a publisher background image sits behind the pane (Figma 9007:13101). */
export const OFFERS_PANE_SURFACE_WITH_BACKGROUND_CLASS = 'bg-[rgba(0,0,0,0.1)]';

/** Flat details surface when no publisher background is shown (Figma 9028:15747). */
export const OFFERS_PANE_SURFACE_DEFAULT_CLASS = 'bg-[rgba(0,0,0,0.04)]';

export const getOffersPaneSurfaceClass = (hasPublisherBackground: boolean): string =>
  hasPublisherBackground ? OFFERS_PANE_SURFACE_WITH_BACKGROUND_CLASS : OFFERS_PANE_SURFACE_DEFAULT_CLASS;

/** Max offer thumbnails shown in the desktop stack preview (Figma 9007:13101). */
export const OFFER_STACK_VISIBLE = 3;

/** Scrollable offer rows on desktop (Figma 9028:15746 `items` 426px minus ~56px pane header). */
export const OFFERS_LIST_MAX_HEIGHT_PX = 370;

/** Scrollable offer rows on portrait mobile (shorter viewport keeps price + CTA above the fold). */
export const OFFERS_LIST_MAX_HEIGHT_MOBILE_PX = 190;

/**
 * Scrollable offer rows on landscape mobile. Figma 9007:14585 clips the list to ~79px
 * so only a peek of one row is visible; the Details column below (promo + totals + CTA)
 * needs the rest of the short landscape viewport. Increase later if UX asks for more.
 */
export const OFFERS_LIST_MAX_HEIGHT_LANDSCAPE_PX = 80;

/**
 * Resolves the offer-list scroll cap by viewport band.
 *
 * `isLandscape` (phone-landscape only, `700 ≤ w ≤ 1023 && h ≤ 500 && w > h` —
 * see `isLandscape` in `utils/device`) wins over `isMobile` so a landscape
 * phone gets the tight 80px peek rather than portrait's 190px. Desktop stays
 * at 370px. Kept in JS (not Tailwind's `landscape:` variant) so the band
 * matches the app's `isLandscape` exactly — Tailwind's `landscape:` would also
 * match wide desktops in landscape orientation.
 */
export const getOffersListMaxHeightPx = ({
  isLandscape,
  isMobile,
}: {
  isLandscape: boolean;
  isMobile: boolean;
}): number => {
  if (isLandscape) return OFFERS_LIST_MAX_HEIGHT_LANDSCAPE_PX;
  if (isMobile) return OFFERS_LIST_MAX_HEIGHT_MOBILE_PX;
  return OFFERS_LIST_MAX_HEIGHT_PX;
};

export const getOfferStackPreview = (lineItems: CartLineItem[]): CartLineItem[] =>
  lineItems.slice(0, OFFER_STACK_VISIBLE);

export const getOfferOverflowCount = (lineItems: CartLineItem[]): number =>
  Math.max(0, lineItems.length - OFFER_STACK_VISIBLE);

/** Multi-offer cart checkout — OffersPane replaces legacy offer name + purchased items list on both viewports. */
export const isMultiOfferCart = (lineItems: CartLineItem[]): boolean => lineItems.length > 1;

export const getCartLineItemKey = (lineItem: CartLineItem, index: number): string =>
  lineItem.offer.id ?? lineItem.offer.sku ?? String(index);

export const getCartLineItemDisplayName = (lineItem: CartLineItem): string =>
  lineItem.offer.displayName ?? lineItem.offer.name;

/** Offer image, falling back to the first nested product asset when absent. */
export const getCartLineItemAssetUrl = (lineItem: CartLineItem): string =>
  lineItem.offer.assetUrl || lineItem.offer.products?.[0]?.assetUrl || '';

/** How many times this offer row appears in the cart (`CartLineItem.quantity`). */
export const getCartLineItemQuantityLabel = (lineItem: CartLineItem): string => `x${lineItem.quantity}`;

/** Hide the thumbnail `xN` badge when the offer was only added once. */
export const shouldShowCartLineItemQuantityBadge = (lineItem: CartLineItem): boolean => lineItem.quantity > 1;
